import { inject, Injectable } from '@angular/core';
import { selectAllEntities, setEntities } from '@ngneat/elf-entities';
import { UserRecentService } from '@thiememeulenhoff/ng-api-gateway';
import { RecentSubject, UserRecent } from '@thiememeulenhoff/types';
import { map, tap } from 'rxjs';
import * as stores from './user-recent.store';

@Injectable({
  providedIn: 'root',
})
export class UserRecentFacade {
  readonly #userRecents = stores.userRecents;

  readonly #userRecentService = inject(UserRecentService);

  readonly recentMethods$ = this.#getRecentsBySubject(RecentSubject.Method);

  readonly recentArrangements$ = (methodSlug: string) =>
    this.#getRecentsBySubject(RecentSubject.Arrangement, methodSlug);

  readonly recentResource$ = (arrangementCode: string) =>
    this.#getRecentsBySubject(RecentSubject.Resource, arrangementCode).pipe(
      map((resources) => {
        if (resources.length === 0) return null;

        return resources[0];
      }),
    );

  #getRecentsBySubject(subject: RecentSubject, parentIdentifier?: string) {
    return this.#userRecents.pipe(
      selectAllEntities(),
      map((entities: Array<UserRecent>) =>
        entities
          .filter((entity) => {
            return (
              entity.subject === subject &&
              (parentIdentifier
                ? entity.parentIdentifier === parentIdentifier
                : true)
            );
          })
          .sort((a, b) => {
            return new Date(a.createdAt) < new Date(b.createdAt) ? 1 : -1;
          }),
      ),
    );
  }

  setRecentMethod(methodSlug: string) {
    return this.#userRecentService
      .createRecentMethod({
        subjectIdentifier: methodSlug,
      })
      .pipe(
        tap((result) =>
          this.setRecents([
            ...result.recentArrangements,
            ...result.recentMethods,
            ...result.recentResources,
          ]),
        ),
      );
  }

  setRecentArrangement(
    methodSlug: string,
    arrangementContentIdentifier: string,
  ) {
    return this.#userRecentService
      .createRecentArrangement(methodSlug, {
        subjectIdentifier: arrangementContentIdentifier,
      })
      .pipe(
        tap((result) =>
          this.setRecents([
            ...result.recentArrangements,
            ...result.recentMethods,
            ...result.recentResources,
          ]),
        ),
      );
  }

  setRecentResource(arrangementCode: string, contentIdentifier: string) {
    return this.#userRecentService
      .createRecentResource(arrangementCode, {
        subjectIdentifier: contentIdentifier,
      })
      .pipe(
        tap((result) =>
          this.setRecents([
            ...result.recentArrangements,
            ...result.recentMethods,
            ...result.recentResources,
          ]),
        ),
      );
  }

  setRecents(userRecents: Array<UserRecent>) {
    this.#userRecents.update(setEntities(userRecents));
  }

  removeAllRecents() {
    return this.#userRecentService
      .removeAllRecents()
      .pipe(tap(() => this.setRecents([])));
  }
}
